import React from "react";
import TinyGradient from "tinygradient";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { GradientBar } from "./GradientBar";
import { GradientColors } from "./constants";
import {
  getIndexForValue,
  getIndexRangeText,
  getIndicatorIndices,
} from "./indices";
import { Card } from "~/components/ui/card";
import { cn } from "~/lib/utils";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Info } from "lucide-react";

export interface IndicatorCardProps {
  value: number;
  /** Stable indicator-definition name (e.g. "P"), used to select indices. */
  indicatorName: string;
  /** Name shown to users; falls back to `indicatorName`. */
  indicatorDisplayName?: string;
  indicatorDescription: string;
  decimalPlaces: number;
  minValue: number;
  maxValue: number;
  indicatorUnit?: string;
  reversed?: boolean;
}

export const IndicatorCard: React.FC<IndicatorCardProps> = (props) => {
  const {
    color,
    indices,
    valueText,
    indexName,
    rangeText,
  } = useIndicatorLogic(props);
  const displayName = props.indicatorDisplayName || props.indicatorName;

  return (
    <Card className="overflow-hidden border-l-8" style={{ borderLeftColor: color }}>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="item-1" className="border-0">
          <AccordionTrigger className={cn("hover:no-underline py-4 px-4")}>
            <div className="flex flex-col items-start text-left w-full">
              <span className="text-base font-semibold">
                {displayName}
              </span>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-lg font-medium">
                  {valueText}
                </span>
                {props.indicatorUnit && (
                  <span className="text-sm text-muted-foreground">
                    {props.indicatorUnit}
                  </span>
                )}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="flex flex-col gap-6">
              <GradientBar
                name={displayName}
                indexNames={indices.names}
                value={props.value}
                minValue={indices.displayValues[0].start}
                maxValue={
                  indices.displayValues[indices.displayValues.length - 1].end
                }
                decimalPlaces={props.decimalPlaces}
                breakpoints={indices.breakpoints}
                displayValues={indices.displayValues}
                reversed={props.reversed}
              />
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  {props.indicatorDescription}
                </p>
                <Alert className="mt-2 items-center">
                  <Info className="h-4 w-4 !-translate-y-0.5" />
                  <AlertDescription>
                    <p>
                      This sample has a {indices.indexName} index of{" "}
                      <strong className="text-foreground">{indexName}</strong> for{" "}
                      {displayName}, which indicates a value in the range{" "}
                      <strong className="text-foreground">
                        {rangeText}
                        {props.indicatorUnit}
                      </strong>
                      .
                    </p>
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

const useIndicatorLogic = (props: IndicatorCardProps) => {
  const position = Math.min(
    1,
    Math.max(
      0,
      (props.value - props.minValue) / (props.maxValue - props.minValue)
    )
  );
  const gradient = new TinyGradient(GradientColors);
  const color = gradient
    .rgbAt(props.reversed ? 1 - position : position)
    .toHexString();

  const indices = getIndicatorIndices(
    props.indicatorName,
    props.minValue,
    props.maxValue
  );

  const firstDisplay = indices.displayValues[0];
  const lastDisplay = indices.displayValues[indices.displayValues.length - 1];

  let valueText = props.value.toFixed(props.decimalPlaces);
  if (props.value < firstDisplay.start) {
    valueText = "<" + firstDisplay.start.toFixed(props.decimalPlaces);
  } else if (props.value > lastDisplay.end) {
    valueText = ">" + lastDisplay.end.toFixed(props.decimalPlaces);
  }

  const index = getIndexForValue(indices, props.value);
  const indexName = indices.names[index];
  const rangeText = getIndexRangeText(indices, index, props.decimalPlaces);

  return {
    color,
    indices,
    valueText,
    indexName,
    rangeText,
  };
};